/**
 * @module  public/js/stores/tasksstore.js
 * @class   TasksStore
 * @summary Reactive in-memory cache and CRUD layer for CRM tasks.
 *
 * @description
 * Maintains and memoises every task for home, account, deal, or contact contexts while staying framework-agnostic.
 * Provides context-aware CRUD helpers and emits change events so UI layers remain simple and responsive.
 *
 * ## Key Features
 * – Loads tasks per context and caches results to minimise network calls
 * – Handles create, update, delete with optimistic cache mutation
 * – Lightweight pub/sub so controllers automatically react to data changes
 *
 * ## Public API
 * – `<load({contextType, contextId, force})>` — fetch tasks and populate cache
 * – `<get({contextType, contextId})>` — return cached task array
 * – `<create({contextType, contextId, payload})>` — POST new task and update store
 * – `<update(taskId, patch)>` — PATCH task and merge into every cache list
 * – `<delete(taskId)>` — remove task from backend and purge cache
 * – `<set(context, tasks)>` — seed cache manually without network fetch
 * – `<on(event, cb)>` / `<off(event, cb)>` — subscribe/unsubscribe to “change” events
 *
 * ## Dependencies
 * { apiClient, authService, crmService, endpoints }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

export default class TasksStore {
  /* ────── DI singletons ────── */
  #apiClient;
  #auth;
  #crmConn;
  #endpoints;
  constructor({ apiClient, authService, crmConnectionService, endpoints }) {
    if (!apiClient || !authService || !crmConnectionService || !endpoints) {
      throw new Error('TasksStore: missing dependencies');
    }
    this.#apiClient = apiClient;
    this.#auth = authService;
    this.#crmConn = crmConnectionService;
    this.#endpoints = endpoints;
  }

  /* ────── public facade ────── */
  async getTasks(context = {}, force = false) {
    const { type, id } = this.#ctx(context);
    await this.#load({ type, id, force });
    return this.#get({ type, id });
  }

  /* ────── CRUD mapped to CFs ────── */
  async createTask(payload, context = {}, options = {}) {
    const { userId, crm, tenant: tenantId } = await this.#ident();
    const { type, id: ctxId } = this.#ctx(context);
    const url = this.#endpoints.manageUserTask();

    const body = this.#cleanPayload({
      action: 'create',
      userId,
      crm,
      tenantId,
      subject: payload.subject,
      status: payload.status,
      description: payload.description,
      dueDate: this.#toDueDateValue(payload.dueDate ?? payload.dueDateISO),
      associatedCompanyId: payload.companyId,
      associatedContactId: payload.contactId,
      metadata: payload.metadata,
      contextType: type,
      contextId: ctxId,
      skipCrmCreates:
        options.skipCrmCreates === undefined
          ? undefined
          : !!options.skipCrmCreates,
    });

    if (location.hostname === 'localhost') {
      console.debug('[TasksStore] POST', url, { keys: Object.keys(body) });
    }
    const resp = await this.#apiClient.fetchJson(url, {
      auth: true,
      method: 'POST',
      body,
    });
    const taskId = resp?.taskId ?? null;
    if (!taskId) return null;

    const key = this.#key(type, ctxId);
    const list = this.#cache.get(key) ?? [];
    list.unshift({
      id: taskId,
      subject: payload.subject,
      status: payload.status,
      dueDateISO: payload.dueDateISO ?? '',
      description: payload.description ?? '',
      company: { id: payload.companyId ?? '', name: '' },
      contact: { id: payload.contactId ?? '', name: '' },
      metadata: payload.metadata ?? null,
    });
    this.#cache.set(key, list);
    this.#emit();
    return taskId;
  }

  async updateTask(taskId, patch, identityOverrides = {}) {
    const {
      userId,
      crm,
      tenant: tenantId,
    } = await this.#ident(identityOverrides);
    const url = this.#endpoints.manageUserTask();

    const body = this.#cleanPayload({
      action: 'update',
      userId,
      crm,
      tenantId,
      taskId,
      subject: patch.subject,
      status: patch.status,
      description: patch.description,
      dueDate: this.#toDueDateValue(patch.dueDate ?? patch.dueDateISO),
      associatedCompanyId: patch.companyId,
      associatedContactId: patch.contactId,
      scheduleFollowUp: patch.scheduleFollowUp,
    });

    if (location.hostname === 'localhost') {
      console.debug('[TasksStore] POST', url, { keys: Object.keys(body) });
    }
    const resp = await this.#apiClient.fetchJson(url, {
      auth: true,
      method: 'POST',
      body,
    });
    const id = resp?.taskId ?? taskId;
    for (const [, list] of this.#cache.entries()) {
      const i = list.findIndex((t) => t.id === id);
      if (i === -1) continue;
      const orig = list[i];
      const updated = { ...orig };
      if (patch.subject !== undefined) updated.subject = patch.subject;
      if (patch.status !== undefined) updated.status = patch.status;
      if (patch.dueDateISO !== undefined) updated.dueDateISO = patch.dueDateISO;
      if (patch.description !== undefined)
        updated.description = patch.description;
      if (patch.companyId !== undefined)
        updated.company = { ...orig.company, id: patch.companyId };
      if (patch.contactId !== undefined)
        updated.contact = { ...orig.contact, id: patch.contactId };
      list[i] = updated;
    }
    this.#emit();
    return id;
  }

  async deleteTask(taskId) {
    const { userId, crm, tenant: tenantId } = await this.#ident();
    const url = this.#endpoints.manageUserTask();
    const body = this.#cleanPayload({
      action: 'delete',
      userId,
      crm,
      tenantId,
      taskId,
    });

    if (location.hostname === 'localhost') {
      console.debug('[TasksStore] POST', url, { keys: Object.keys(body) });
    }
    const resp = await this.#apiClient.fetchJson(url, {
      auth: true,
      method: 'POST',
      body,
    });
    const id = resp?.taskId ?? taskId;
    for (const [k, arr] of this.#cache)
      this.#cache.set(
        k,
        arr.filter((t) => t.id !== id)
      );
    this.#emit();
    return id;
  }

  /**
   * Seed the cache for a given context without fetching from the backend.
   * Primarily used by admin tools to impersonate another user.
   *
   * @param {Object} context – { accountId?, contactId? } identifying the scope
   * @param {Array<Object>} tasks – raw CRM task objects
   */
  set(context = {}, tasks = []) {
    const { type, id } = this.#ctx(context);
    const key = this.#key(type, id);
    this.#cache.set(key, tasks.map(canonicalTask));
    if (context.userId) {
      this.#overrideIdent = {
        userId: context.userId,
        crm: context.crm ?? null,
        tenant: context.tenant ?? null,
      };
    }
    this.#emit();
  }

  clearImpersonation() {
    this.#overrideIdent = null;
  }

  async completeTask(taskId, followUp = false) {
    return this.updateTask(taskId, {
      status: 'COMPLETED',
      scheduleFollowUp: followUp,
    });
  }

  /* ────── internal fetch/cache ────── */
  async #load({ type, id, force }) {
    const key = this.#key(type, id);
    if (!force && this.#cache.has(key)) return;

    const { userId, crm, tenant: tenantId } = await this.#ident();
    const url = this.#endpoints.getUserTasks({
      userId,
      contextType: type,
      contextId: id,
      crm,
      ...(tenantId ? { tenant: tenantId } : {}),
    });
    const { tasks = [] } = await this.#apiClient.fetchJson(url, {
      method: 'GET',
    });

    this.#cache.set(key, tasks.map(canonicalTask));
    this.#emit();
  }

  /* ────── helpers & pub/sub ────── */
  #cache = new Map();
  #listeners = { change: new Set() };
  #overrideIdent = null;

  #key(t, i) {
    return `${t}:${i || ''}`;
  }
  #get(o) {
    return this.#cache.get(this.#key(o.type, o.id)) ?? [];
  }

  #emit() {
    const snapshot = [...this.#cache.values()].flat();
    this.#listeners.change.forEach((fn) => {
      try {
        fn(snapshot);
      } catch (err) {
        void err;
        /* ignore subscriber errors */
      }
    });
  }
  subscribe(fn) {
    this.#listeners.change.add(fn);
    fn([...this.#cache.values()].flat());
    return () => this.#listeners.change.delete(fn);
  }
  on(evt, fn) {
    this.#listeners[evt]?.add(fn);
  }
  off(evt, fn) {
    this.#listeners[evt]?.delete(fn);
  }

  #ctx({ userId, accountId, contactId }) {
    // ── NEW branch ──
    if (userId) return { type: 'user', id: userId };

    if (accountId) return { type: 'account', id: accountId };
    if (contactId) return { type: 'contact', id: contactId };
    return { type: 'home', id: '' };
  }
  /**
   * Determine the current identity and tenant.
   *
   * Order:
   * 1) Explicit override via {@link set}
   * 2) Custom claims from fresh ID token (tenant or tenantId)
   * 3) Auth instance's tenantId
   * 4) sessionStorage `topsail__tenant`
   */
  async #ident(overrides = {}) {
    const overrideTenant = overrides.tenant ?? overrides.tenantId ?? null;

    if (this.#overrideIdent) {
      return {
        userId: overrides.userId ?? this.#overrideIdent.userId,
        crm: overrides.crm ?? this.#overrideIdent.crm,
        tenant: overrideTenant ?? this.#overrideIdent.tenant,
      };
    }

    const u = await this.#auth.requireAuth();
    const resolvedUserId = overrides.userId ?? u.uid;

    let crm = overrides.crm ?? null;
    if (!crm) {
      try {
        crm = await this.#crmConn.getConnectedProvider(resolvedUserId);
      } catch (err) {
        console.warn('[TasksStore.ident] CRM lookup failed', err);
        crm = await this.#crmConn.getConnectedProvider();
      }
    }

    let tenant = overrideTenant;
    try {
      if (!tenant) {
        const token = await u.getIdTokenResult(true);
        tenant = token.claims.tenant || token.claims.tenantId || null;
      }
    } catch (err) {
      console.warn('[TasksStore.ident] token claim read failed', err);
    }

    if (!tenant) tenant = u.tenantId ?? null;
    if (!tenant) {
      try {
        tenant = sessionStorage.getItem('topsail__tenant');
      } catch (err) {
        void err;
        /* ignore */
      }
    }

    return { userId: resolvedUserId, crm, tenant };
  }
  /**
   * Remove undefined properties so we only send defined fields to manageUserTask.
   *
   * @param {Object} obj – raw payload candidates with optional fields
   * @returns {Object} payload without undefined props
   */
  #cleanPayload(obj) {
    const cleaned = { ...obj };
    if (cleaned.tenantId === undefined || cleaned.tenantId === null) {
      delete cleaned.tenantId;
    }
    for (const key of Object.keys(cleaned)) {
      if (cleaned[key] === undefined) delete cleaned[key];
    }
    return cleaned;
  }
  /**
   * Normalise due date inputs into millis or ISO strings for manageUserTask.
   *
   * @param {string|number|Date|null|undefined} value – incoming due date input
   * @returns {number|string|null|undefined} formatted value for API consumption
   */
  #toDueDateValue(value) {
    if (value === undefined) return undefined;
    if (value === null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const numeric = Number(trimmed);
      if (!Number.isNaN(numeric) && String(numeric) === trimmed) {
        return numeric;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const dt = new Date(`${trimmed}T00:00:00`);
        if (!Number.isNaN(dt.valueOf())) return dt.getTime();
      }
      const dt = new Date(trimmed);
      if (!Number.isNaN(dt.valueOf())) return dt.toISOString();
      return trimmed;
    }
    return undefined;
  }
}

/* =====================================================================
 * canonicalTask – map raw HubSpot / Salesforce task → UI-friendly shape
 * =================================================================== */
function canonicalTask(raw) {
  /* ---------- Scalars ---------- */
  const subject =
    raw.Subject ?? raw.properties?.hs_task_subject ?? '(No subject)';

  const status = raw.Status ?? raw.properties?.hs_task_status ?? '';
  const taskId = raw.taskId ?? raw.Id ?? raw.id ?? '';

  /* ---------- Due date ---------- */
  let dueDateISO = '';
  if (raw.ActivityDate) {
    // Salesforce string yyyy-mm-dd
    dueDateISO = raw.ActivityDate;
  } else if (raw.properties?.hs_timestamp) {
    // HubSpot millis
    const d = new Date(raw.properties.hs_timestamp);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    dueDateISO = d.toISOString().slice(0, 10);
  }

  /* ---------- Company / Contact ---------- */
  const company = {
    id: raw.associated_company_id || raw.companyId || '',
    name: raw.associated_company_name || raw.companyName || '',
  };
  const contact = {
    id: raw.associated_contact_id || raw.contactId || '',
    name: raw.associated_contact_name || raw.contactName || '',
  };

  /* ---------- Scheduling metadata ---------- */
  const fixed = raw.fixed ?? raw.metadata?.fixed ?? null;
  const maxLag = raw.maxLag ?? raw.metadata?.maxLag ?? null;
  const topUp = raw.topUp ?? raw.metadata?.topUp ?? raw.metadata?.topup ?? null;

  let queueDateISO = '';
  const queueCandidate =
    raw.queueDateISO ||
    raw.queueDate ||
    raw.queuedForISO ||
    raw.queuedFor ||
    raw.scheduledForISO ||
    raw.scheduledFor ||
    raw.scheduledDateISO ||
    raw.scheduledDate ||
    raw.metadata?.queueDateISO ||
    raw.metadata?.queueDate ||
    raw.metadata?.queuedForISO ||
    raw.metadata?.queuedFor ||
    raw.metadata?.scheduledForISO ||
    raw.metadata?.scheduledFor ||
    raw.metadata?.scheduledDateISO ||
    raw.metadata?.scheduledDate ||
    null;

  if (queueCandidate instanceof Date) {
    queueDateISO = queueCandidate.toISOString().slice(0, 10);
  } else if (queueCandidate?.toDate) {
    queueDateISO = queueCandidate.toDate().toISOString().slice(0, 10);
  } else if (
    queueCandidate &&
    typeof queueCandidate === 'object' &&
    typeof queueCandidate._seconds === 'number'
  ) {
    const millis = queueCandidate._seconds * 1000;
    const extra = Math.floor((queueCandidate._nanoseconds || 0) / 1e6);
    queueDateISO = new Date(millis + extra).toISOString().slice(0, 10);
  } else if (
    typeof queueCandidate === 'number' &&
    Number.isFinite(queueCandidate)
  ) {
    queueDateISO = new Date(queueCandidate).toISOString().slice(0, 10);
  } else if (typeof queueCandidate === 'string') {
    const trimmed = queueCandidate.trim();
    if (trimmed) {
      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.valueOf())) {
        queueDateISO = parsed.toISOString().slice(0, 10);
      }
    }
  }

  return {
    id: raw.Id ?? raw.id,
    taskId,
    subject,
    status,
    dueDateISO,
    description: raw.Description ?? raw.properties?.hs_task_body ?? '',
    company,
    contact,
    fixed,
    maxLag,
    topUp,
    queueDateISO,
  };
}
